import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { hasAnyDraft } from '@/lib/draft';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Shell } from '@/screens/Login';

/**
 * L'ultima rete.
 *
 * Senza un confine di errore una sola eccezione — in render o nel commit, che
 * è il modo in cui è successo il 14/08/2026 — fa smontare a React l'intero
 * root: pagina bianca, nessun messaggio, nessuna azione possibile. Per chi sta
 * in un COC alle tre di notte una console bianca e una console spenta sono la
 * stessa cosa, e la reazione giusta — pubblicare con gli altri canali del
 * Comune — non gliela suggerisce nessuno.
 *
 * La causa di quel giorno è chiusa (`Layout.tsx`). Questa classe serve per la
 * prossima, che non sappiamo quale sia.
 *
 * Tre cose da dire, in quest'ordine, perché è l'ordine in cui servono:
 *
 *   1. il guasto è **qui**, non sugli avvisi già pubblicati. Finché un
 *      operatore non lo sa, nient'altro che gli diciamo conta;
 *   2. il bottone che ricarica;
 *   3. attaccato al bottone, che la bozza in composizione sopravvive al
 *      ricaricamento (`lib/draft.ts`): è la paura che trattiene dal premerlo,
 *      e va sciolta dove sta la mano, non in cima alla pagina.
 *
 * Deve reggersi da sola: solo componenti di presentazione, niente router,
 * niente sessione, niente query. Se il ripiego avesse bisogno di qualcosa che
 * si è appena rotto, tornerebbe la pagina bianca — con un passaggio in più.
 *
 * Non cattura tutto, ed è un limite di React, non una scelta: quello che
 * succede dentro un gestore di evento o in una promessa non passa di qui. Gli
 * errori di rete infatti li mostrano le schermate, sullo stato della query che
 * li ha causati, che è dove hanno un senso.
 */

interface Props {
  children: ReactNode;
}

interface State {
  /** Distinto dall'errore: React può consegnarci anche un `throw undefined`. */
  crashed: boolean;
  error: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { crashed: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { crashed: true, error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    // L'unica traccia che questo guasto lascerà: telemetria non ce n'è, ed è
    // una decisione scritta nel README. Resta nella console del browser per
    // chi, al telefono, chiederà di aprirla — la riga corta e leggibile,
    // quella che l'operatore può dettare, sta invece nella schermata.
    console.error('[am-console] errore non gestito', error, info.componentStack);
  }

  override render() {
    if (!this.state.crashed) return this.props.children;
    return <Crashed error={this.state.error} />;
  }
}

/* -------------------------------------------------------------------------- */

function Crashed({ error }: { error: unknown }) {
  // La bozza si nomina solo se esiste davvero. Rassicurare sul lavoro salvato
  // chi non stava scrivendo niente sposta l'attenzione sulla cosa sbagliata, e
  // questo messaggio deve arrivare intero a chi invece stava scrivendo.
  const draft = hasAnyDraft();

  // Ricaricare una rotta che si rompe da sé riporta alla stessa schermata
  // rotta. Da lì l'unica uscita è la home, che è anche la schermata da cui si
  // vede cosa c'è in corso.
  const elsewhere = window.location.pathname !== '/';

  return (
    <Shell>
      <Card>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>Si è rotto qualcosa nella console</AlertTitle>
            <AlertDescription>
              Il guasto è qui, non sugli avvisi: quelli già pubblicati restano
              visibili in app e scadono da soli come previsto.
            </AlertDescription>
          </Alert>
        </CardContent>

        <CardFooter className="flex-col items-stretch gap-2">
          <Button size="lg" onClick={() => window.location.reload()}>
            <RotateCcw />
            Ricarica la pagina
          </Button>
          {elsewhere && (
            <Button variant="ghost" onClick={() => window.location.assign('/')}>
              Torna allo stato
            </Button>
          )}
          {/* Fuori dal riquadro rosso, e di proposito: il rosso porta il
              guasto, non la buona notizia. Qui sotto sta anche perché è qui
              che serve — la legge chi esita davanti al bottone. */}
          {draft && (
            <p className="text-[13px] leading-snug text-ink3">
              Quello che stavi scrivendo resta salvato: lo ritrovi dov’era.
            </p>
          )}
        </CardFooter>
      </Card>

      {/* I due casi che il riquadro non deve portarsi dentro: chi è stato
          interrotto proprio mentre pubblicava, e chi ricarica e si ritrova
          qui. Il primo ha bisogno di un fatto — cos'è uscito davvero — non di
          una rassicurazione. */}
      <p className="mt-5 text-[13px] leading-relaxed text-ink3">
        Se il guasto è arrivato mentre pubblicavi, dopo il ricaricamento apri lo
        storico: dice quello che è uscito davvero. Se succede di nuovo, pubblica
        con gli altri canali del Comune e scrivi a chi ti ha dato l’accesso,
        riportando questa riga.
      </p>
      <p className="mt-2 select-all rounded-md border bg-card px-3 py-2 text-[12px] leading-relaxed text-ink3">
        {describe(error)}
      </p>
    </Shell>
  );
}

/**
 * La riga da dettare al telefono. Mai vuota: una schermata di guasto che alla
 * domanda «cosa dice?» non risponde niente vale quanto quella bianca.
 *
 * In produzione il codice è minificato e lo stack non aiuterebbe nessuno — il
 * messaggio sì: il guasto del 14/08 si presentava proprio come una riga di
 * questo tipo.
 */
function describe(error: unknown): string {
  if (error instanceof Error) {
    return error.message ? `${error.name}: ${error.message}` : error.name;
  }
  if (typeof error === 'string' && error.trim()) return error;
  return 'Errore senza descrizione';
}
